import { closestCenter, DndContext } from '@dnd-kit/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { usePricing } from '../../contexts/PricingContext';
import { useTeamLimits } from '../../hooks/useTeamLimits';
import type { Tender } from '../../types/tender';
import BidStrategyCard from './BidStrategyCard';
import AddSectionDialog from './ReviewSections/AddSectionDialog';
import AssignmentDialog from './ReviewSections/AssignmentDialog';
import { DeleteSectionDialog } from './ReviewSections/DeleteSectionDialog';
import { FloatingSectionChat } from './ReviewSections/FloatingSectionChat';
import HistoryDialog from './ReviewSections/HistoryDialog';
import { QuickReviewBanner } from './ReviewSections/QuickReviewBanner';
import { TenderSectionsList } from './ReviewSections/TenderSectionsList';
import { UsageGuide } from './ReviewSections/UsageGuide';
import { countWords, isExcluded } from './ReviewSections/utils';
import { WordCountSummary } from './ReviewSections/WordCountSummary';
import { useSectionReviews } from './useSectionReviews';

interface ReviewSectionsStepProps {
  tenderId: string;
  tender: Tender;
  onQuickReview: () => void;
  isQuickReviewing: boolean;
  isCompiling: boolean;
  onCompletionChange: (isComplete: boolean) => void;
  actionSectionId?: string | null;
}

const ReviewSectionsStep: React.FC<ReviewSectionsStepProps> = ({
  tenderId,
  tender,
  onQuickReview,
  isQuickReviewing,
  isCompiling,
  onCompletionChange,
  actionSectionId,
}) => {
  const theme = useTheme();
  const { account, hasPermission } = useAccount();
  const { pricingSettings } = usePricing();

  const [hasNewTips, setHasNewTips] = useState(false);
  const prevIsReviewingRef = useRef(isQuickReviewing);

  useEffect(() => {
    if (prevIsReviewingRef.current && !isQuickReviewing) {
      setHasNewTips(true);
    }
    prevIsReviewingRef.current = isQuickReviewing;
  }, [isQuickReviewing]);

  const reviewState = useSectionReviews({
    tenderId,
    tender,
    account: account as import('../../types/auth').User | null,
    hasPermission,
    actionSectionId,
    onCompletionChange,
  });

  const {
    sections,
    visibleSections,
    loading,
    isOwner,
    canEditAllSections,
    assignDialogOpen,
    setAssignDialogOpen,
    sectionToAssign,
    teamUsers,
    assignLoading,
    handleAssignUser,
    sensors,
    activeId,
    expandedSectionIds,
    setExpandedSectionIds,
    handleDragStart,
    handleDragEnd,
    editingSectionId,
    downloadingSectionId,
    editContent,
    setEditContent,
    showDiffs,
    setShowDiffs,
    activeEditor,
    setActiveEditor,
    chatSectionId,
    setChatSectionId,
    isChatMinimized,
    setIsChatMinimized,
    chatContextContent,
    inlineResolved,
    deleteDialogOpen,
    setDeleteDialogOpen,
    sectionToDelete,
    historyDialogOpen,
    setHistoryDialogOpen,
    historyRevisions,
    historyLoading,
    comparisonData,
    addDialogOpen,
    setAddDialogOpen,
    newSectionTitle,
    setNewSectionTitle,
    newSectionLevel,
    setNewSectionLevel,
    handleConfirmAddSection,
    handleConfirmDelete,
    handleSelectRevision,
    unlock,
    assign,
  } = reviewState;

  const totalWordCount = useMemo(
    () => sections.reduce((sum, section) => sum + countWords(section.content), 0),
    [sections]
  );

  const coreWordCount = useMemo(
    () =>
      sections
        .filter((s) => !isExcluded(s.title))
        .reduce((sum, section) => sum + countWords(section.content), 0),
    [sections]
  );

  const { checkLimit } = useTeamLimits();
  const { isLimitReached } = useMemo(
    () => checkLimit('reviews', tender.reviewCount || 0),
    [checkLimit, tender.reviewCount]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sections.length === 0) {
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography>No sections generated yet. Please generate a draft first.</Typography>
      </Box>
    );
  }

  const sectionHandlers = {
    handleSave: reviewState.handleSave,
    handleCancelEdit: reviewState.handleCancelEdit,
    handleStatusChange: reviewState.handleStatusChange,
    handleDeleteClick: reviewState.handleDeleteClick,
    handleOpenAssignDialog: reviewState.handleOpenAssignDialog,
    assign,
    handleRejectSuggestion: reviewState.handleRejectSuggestion,
    handleAcceptSuggestion: reviewState.handleAcceptSuggestion,
    handleDiffApply: reviewState.handleDiffApply,
    handleUndo: reviewState.handleUndo,
    handleRedo: reviewState.handleRedo,
    handleOpenManualEdit: reviewState.handleOpenManualEdit,
    handleOpenAIEdit: reviewState.handleOpenAIEdit,
    handleDownloadSectionDocx: reviewState.handleDownloadSectionDocx,
    handleApplyAIEdit: reviewState.handleApplyAIEdit,
    handleReceiveSuggestion: reviewState.handleReceiveSuggestion,
    handleUnlock: unlock,
    handleOpenHistory: reviewState.handleOpenHistory,
    handleRestoreRevision: reviewState.handleRestoreRevision,
    handleCancelHistoryComparison: reviewState.handleCancelHistoryComparison,
  };

  const handleNavigateToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setExpandedSectionIds((prev) => {
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <UsageGuide />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <WordCountSummary totalWordCount={totalWordCount} coreWordCount={coreWordCount} />

            <Accordion
              defaultExpanded={false}
              onChange={(_, expanded) => {
                if (expanded) {
                  setHasNewTips(false);
                }
              }}
              sx={{
                mb: 2,
                bgcolor: 'warning.light',
                '& .MuiAccordionSummary-root': { bgcolor: 'background.paper' },
                border: 2,
                borderColor: theme.palette.greyContrast,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightbulbIcon color="warning" />
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                    10 Quick Tips to Improve this Proposal
                  </Typography>
                  {hasNewTips && (
                    <Chip
                      label="New!"
                      color="error"
                      size="small"
                      sx={{ ml: 1, fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
                <BidStrategyCard tender={tender} />
              </AccordionDetails>
            </Accordion>

            <TenderSectionsList
              sections={sections}
              visibleSections={visibleSections}
              activeId={activeId}
              expandedSectionIds={expandedSectionIds}
              comparisonData={comparisonData}
              tenderId={tenderId}
              tender={tender}
              account={account as import('../../types/auth').User | null}
              editingSectionId={editingSectionId}
              editContent={editContent}
              setEditContent={setEditContent}
              activeEditor={activeEditor}
              setActiveEditor={setActiveEditor}
              showDiffs={showDiffs}
              setShowDiffs={setShowDiffs}
              inlineResolved={inlineResolved}
              chatSectionId={chatSectionId}
              setChatSectionId={setChatSectionId}
              downloadingSectionId={downloadingSectionId}
              sectionHandlers={sectionHandlers}
              historyComparisonSectionId={reviewState.historyComparisonSectionId}
              canEditAllSections={canEditAllSections}
              setExpandedSectionIds={setExpandedSectionIds}
              handleOpenAddDialog={reviewState.handleOpenAddDialog}
            />
          </Grid>
        </Grid>
      </DndContext>

      <QuickReviewBanner
        isOwner={isOwner}
        onQuickReview={onQuickReview}
        isQuickReviewing={isQuickReviewing}
        isCompiling={isCompiling}
        isLimitReached={isLimitReached}
        pricingSettings={pricingSettings}
        sections={sections}
        reviewStatus={
          tender.reviewResponse?.lastType === 'quick' ? tender.reviewResponse?.status : undefined
        }
        reviewProgress={
          tender.reviewResponse?.lastType === 'quick' ? tender.reviewResponse?.progress : undefined
        }
      />

      <FloatingSectionChat
        chatSectionId={chatSectionId}
        isChatMinimized={isChatMinimized}
        setIsChatMinimized={setIsChatMinimized}
        setChatSectionId={setChatSectionId}
        tenderId={tenderId}
        tender={tender}
        canEditAllSections={canEditAllSections}
        chatContextContent={chatContextContent}
        sections={sections}
        handleApplyAIEdit={reviewState.handleApplyAIEdit}
        handleReceiveSuggestion={reviewState.handleReceiveSuggestion}
        handleOpenAIEdit={reviewState.handleOpenAIEdit}
        handleUndo={reviewState.handleUndo}
        handleNavigateToSection={handleNavigateToSection}
      />

      <AddSectionDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onConfirm={handleConfirmAddSection}
        title={newSectionTitle}
        onTitleChange={setNewSectionTitle}
        level={newSectionLevel}
        onLevelChange={setNewSectionLevel}
      />

      <AssignmentDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        section={sectionToAssign}
        teamUsers={teamUsers}
        onAssign={handleAssignUser}
        loading={assignLoading}
        currentMemberPermissions={tender.memberPermissions}
        tenderOwnerId={tender.ownerId}
      />

      <HistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        revisions={historyRevisions}
        onSelectRevision={handleSelectRevision}
        isLoading={historyLoading}
      />

      <DeleteSectionDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        sectionToDelete={sectionToDelete}
      />
    </Box>
  );
};

export default ReviewSectionsStep;
